import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { Configure, InstantSearch } from 'react-instantsearch';
import useSimBilling from 'api/sims/useSimBilling';
import useSimProfiles from 'api/sims/useSimProfiles';
import { Loader } from '@hologram-dimension/loader';
import clsx from 'clsx';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import usePlan from 'api/legacy/plans/usePlan';
import { LegacyPlan } from 'types/legacyDashboard';
import useOrganization from 'state/contexts/Organization/useOrganization';
import SearchBox from './SearchBox';
import Hits from './Hits';
import styles from './CoverageSearch.module.css';
import Results from './Results';
import Filters from './Filters';
import {
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_KEY,
  ALGOLIA_INDEX,
  instantsearchRouting,
} from './searchUtils';
import useAlgoliaFilter from './useAlgoliaFilter';
import useAlgoliaMeta from './useAlgoliaMeta';
import { CoverageSearchVariant } from './searchTypes';

const searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);

interface CoverageSearchProps {
  excludeColumns?: string[];
  lastKnownNetwork?: string;
  hologramSimId?: HologramSimId;
  legacyPlan?: LegacyPlan;
  variant?: CoverageSearchVariant;
}
const CoverageSearch = (props: CoverageSearchProps) => {
  const {
    excludeColumns,
    lastKnownNetwork,
    hologramSimId,
    legacyPlan,
    variant = 'default',
  } = props;
  const {
    simBilling,
    error: simBillingError,
    isLoading: isSimBillingLoading,
  } = useSimBilling(hologramSimId);
  const {
    simProfiles,
    error: simProfilesError,
    isLoading: isSimProfilesLoading,
  } = useSimProfiles(hologramSimId);
  const { plan, error: planError, isLoading: isPlanLoading } = usePlan(legacyPlan?.id);
  const organizationId = useOrganization();

  let planProfiles;
  if (legacyPlan) {
    const currentTier = legacyPlan.currentTier ?? 'BASE';
    const currentTierCarriers = Object.keys(
      legacyPlan.tiers?.[currentTier].zones[legacyPlan.zone].carriers ?? {}
    );
    planProfiles = currentTierCarriers
      .map((carrier) => plan?.profiles.find((profile) => profile.carrierId === Number(carrier)))
      .filter((profile) => profile !== undefined);
  }
  const cspFilter = useAlgoliaFilter({ simProfiles, planProfiles });
  const { data: meta, error: metaError, isLoading: isMetaLoading } = useAlgoliaMeta(cspFilter);

  const isLoading = isSimBillingLoading || isSimProfilesLoading || isMetaLoading || isPlanLoading;
  const error = simBillingError || simProfilesError || metaError || planError;

  type Sort = {
    attribute: 'network' | 'country' | 'relevant';
    direction?: 'asc' | 'desc';
  };
  const columns: {
    key: string;
    label: string;
    width?: number;
    sortableAttribute?: Sort['attribute'];
  }[] = [
    { key: 'name', label: 'Network', sortableAttribute: 'network' },
    { key: 'profile', label: 'Profile', width: 125 },
    { key: 'rate', label: 'Rate per MB', width: 100 },
    {
      key: 'country',
      label: 'Country',
      sortableAttribute: 'country',
      width: variant === 'modal' ? 130 : 150,
    },
    {
      key: 'rats',
      label: variant === 'modal' ? 'RAT' : 'Radio Access Technologies (RAT)',
      width: variant === 'modal' ? 180 : 220,
    },
  ];

  if (!legacyPlan && !hologramSimId) {
    return (
      <PanelNotification variant="error" label="No Plan or SIM specified">
        We cannot retrieve network coverage information
      </PanelNotification>
    );
  }
  if (isLoading) {
    return <Loader className={styles.loader}>Loading network coverage</Loader>;
  }
  if (error) {
    return (
      <PanelNotification
        variant="error"
        label="We ran into an issue loading the network coverage details"
      >
        We will keep trying in the background or you can try to reload the page.
      </PanelNotification>
    );
  }
  if (hologramSimId && !simBilling.planId) {
    return (
      <PanelNotification variant="error" label="Cannot retrieve network coverage info">
        The SIM is inactive or does have a data plan
      </PanelNotification>
    );
  }
  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={ALGOLIA_INDEX}
      future={{ preserveSharedStateOnUnmount: true }}
      routing={variant === 'default' ? instantsearchRouting(simProfiles) : undefined}
    >
      {/* TODO: 
    Someday we can highlight the last known network by boosting it 
    with the optionalFilters param below. We just need to add UNC 
    to the last known session and fetch that here 
    */}
      <Configure
        filters={cspFilter || undefined}
        optionalFilters={lastKnownNetwork ? [`code:${lastKnownNetwork}`] : undefined} // how we boost last known network
      />
      <div className={clsx(styles.wrapper, variant === 'modal' && styles.modal)}>
        <div className={styles.filters}>
          <Filters simProfiles={simProfiles} planProfiles={planProfiles} />
        </div>
        <div className={styles.searchWrapper}>
          <div className={styles.searchControls}>
            <SearchBox />
            <Results totalResults={meta?.networks} />
          </div>
          <Hits
            columns={columns}
            lastKnownNetwork={lastKnownNetwork}
            excludeColumns={excludeColumns}
            simProfiles={simProfiles}
            planProfiles={planProfiles}
            orgId={hologramSimId ? simBilling.organizationId : organizationId}
            variant={variant}
            hologramSimId={hologramSimId}
            legacyPlan={legacyPlan}
          />
        </div>
      </div>
    </InstantSearch>
  );
};
export default CoverageSearch;
